import React, { useEffect, useState } from 'react'
import {
    Redirect,
    Route,
    Router,
    Switch,
    useHistory,
    useLocation,
} from 'react-router-dom'
import intl from 'react-intl-universal'
import { useDispatch, useSelector } from 'react-redux'
import { withErrorBoundary } from '@sentry/react'
import { Button, Jumbotron } from 'react-bootstrap'
import { useEventSlug, useFirstRender } from 'hooks'
import ContextualHelpDrawer from 'features/contextual_help/ContextualHelpDrawer'
import ImageLibraryPage from 'features/image_library/ImageLibraryPage'
import ModalContainer from 'features/modals/ModalContainer'
import NotificationsContainer from 'features/notifications/NotificationsContainer'
import { languageMapper, locales, NotFound } from 'components'
import FullPageLoading from 'components/FullPageLoading'
import Layout, { PageContent } from 'components/layout/Layout'
import { eventRouteList, noEventRouteList } from 'routes'
import { RootState } from 'store'
import { RouteProps } from 'types'
import { getRefreshToken, isTokenExpired, _ } from 'utils'
import { checkDuplicatedContext, getEnterEventPath } from 'utils/routes'
import { eventLanguageSelector, getEventPreview } from 'features/auth/eventPreviewSlice'
import { eventSelector, getEventData, requestStatusSelector } from 'features/events/eventSlice'
import ProfileBadge from 'features/profile/ProfileBadge'
import {
    fetchMyOrganization, organizationLanguageSelector
} from 'features/auth/authSlice'
import browserHistory from 'browserHistory'
import MinimizeImportProgress from 'features/export_import/MinimizeImportProgress'
import WebsocketProvider from 'features/websocket/WebsocketProvider'
import MinimizeBulkInvitesProgress from 'features/attendees/components/MinimizeBulkInvitesProgress'

export const spacePath = '/:eventSlug/:type/:spaceSlug/'

interface RoutesSwitchProps {
    isLoggedIn: boolean
}

const reload = () => {
    // https://stackoverflow.com/questions/29884654/button-that-refreshes-the-page-on-click
    // eslint-disable-next-line no-self-assign
    window.location.href = window.location.href
}

const ErrorPage = () => (
    <Jumbotron>
        <h1>Something went wrong</h1>
        <p>
            The issue has been reported to our team.
        </p>
        <p>
            <Button variant="primary" onClick={reload}>Refresh page</Button>
        </p>
    </Jumbotron>
)

const CreateRouteRenderer = ({ route, isLoggedIn, ...props }: { route: RouteProps, isLoggedIn: boolean }) => {
    const authorized = isLoggedIn || route.isPublic
    const Component = withErrorBoundary(route.component || NotFound, { fallback: ErrorPage })

    return !authorized
        ? <Redirect to="/" />
        : <Component {...props} />
}

// Event component should only be rendered if user is logged in.
const Event = ({ isLoggedIn }: { isLoggedIn: boolean }) => {
    const eventSlug = useEventSlug()
    const history = useHistory()
    const location = useLocation()
    const enterEventPath = getEnterEventPath(location.pathname, eventSlug)
    const eventData = useSelector(eventSelector)
    const hasEventAccess = !!eventData
    const requestStatus = useSelector(requestStatusSelector)
    const isFirstRender = useFirstRender()

    // If booth search is in path, redirect to BoothConfig page
    const booth = new URLSearchParams(window.location.search).get('booth')
    if (booth) {
        history.push(`/${eventSlug}/booth/${booth}`)
    }

    // If user isn't event owner, and he/she tries to access the route is not booth route
    // Return NotFound page
    if (requestStatus === 'succeeded' && !hasEventAccess && !location.pathname.startsWith(`/${eventSlug}/booth/`)) {
        return <NotFound />
    }

    if (requestStatus === 'loading') {
        return (
            <FullPageLoading
                text={_('general.website_loading')}
                isAbsolute
                transparentBackground
                fullWidth
            />
        )
    }

    // default page is event settings
    if (isFirstRender && location.pathname === `/${eventSlug}`) {
        return <Redirect to={`/${eventSlug}/general-settings`} />
    }

    return !isLoggedIn
        ? <Redirect to={enterEventPath} />
        : (
            <>
                <Switch>
                    { eventRouteList.map((route, index) => (
                        <Route
                            path={route.path}
                            exact={route.exact}
                            key={index}
                        >
                            <PageContent
                                title={route.title}
                                hasSpaceBreadcrumb={route.hasSpaceBreadcrumb}
                                noBreadcrumb={route.noBreadCrumb}
                                hasBackButton={route.hasBackButton}
                            >
                                <CreateRouteRenderer route={route} isLoggedIn={isLoggedIn} />
                            </PageContent>
                        </Route>
                    ))}
                    <Route path="*" component={NotFound} />
                </Switch>
                <ProfileBadge />
            </>
        )
}

const RoutesSwitch = ({ isLoggedIn }: RoutesSwitchProps) => (
    <Switch>
        { noEventRouteList.map((route, index) => (
            <Route
                path={route.path}
                exact={route.exact}
                key={index}
            >
                <CreateRouteRenderer route={route} isLoggedIn={isLoggedIn} />
            </Route>
        ))}

        <Route path="/:eventSlug" exact={false}>
            <Layout>
                <Event isLoggedIn={isLoggedIn} />
                <WebsocketProvider />
            </Layout>
        </Route>

        <Route path="*" component={NotFound} />
    </Switch>
)

const SideEffects = ({ isLoggedIn }: { isLoggedIn: boolean }) => {
    const eventSlug = useEventSlug()
    const dispatch = useDispatch()
    useEffect(() => {
        if (eventSlug) {
            dispatch(getEventPreview(eventSlug))
        }
    }, [eventSlug, dispatch])

    useEffect(() => {
        if (eventSlug && isLoggedIn) {
            dispatch(getEventData(eventSlug))
        }
    }, [eventSlug, isLoggedIn, dispatch])

    useEffect(() => {
        if (isLoggedIn) {
            dispatch(fetchMyOrganization())
        }
    }, [dispatch, isLoggedIn])

    useEffect(() => {
        const intervalId = setInterval(
            () => {
                const refreshToken = getRefreshToken()
                if (refreshToken && isTokenExpired(refreshToken)) {
                    browserHistory.push('/logout?token-expired=true')
                }
            },
            5*3600*1000, // Re-check token every 5 minutes
        )
        return () => clearInterval(intervalId)
    }, [])

    return null
}

const App = () => {
    const auth = useSelector((state: RootState) => state.auth)
    const eventLocale = useSelector(eventLanguageSelector)
    const [, setLocale] = useState('en-US') // Help to re-render when locale changes
    const organizationLanguage = useSelector(organizationLanguageSelector)
    const organizationLocale = languageMapper[organizationLanguage]
    const selectedLocale = organizationLocale || eventLocale
    const locale = locales[selectedLocale] ? selectedLocale : 'en-US'
    checkDuplicatedContext()
    useEffect(() => {
        // At first time, the eventProvider is not rendered. So we can't get language from event.current
        // then LocaleProvider will use the default locale (en-US) to render the whole app
        // When App is rendered. The eventProvider will fetch event/access and update language of event
        // And LocaleProvider will update InitOptions with language of event.

        // Has a weird issue here is when LocaleProvider is updated, the intl.getInitOptions().currentLocale is still en-US
        // Set state here to help this component re-render and update locale.
        if (locale !== intl.getInitOptions().currentLocale) {
            setLocale(locale)
        }
    }, [locale])

    return auth.isLoadingAuth
        ? <FullPageLoading text={_('general.website_loading')} />
        : (
            <Router history={browserHistory}>
                <NotificationsContainer />
                <ModalContainer />
                <RoutesSwitch isLoggedIn={auth.isLoggedIn} />
                <SideEffects isLoggedIn={auth.isLoggedIn} />
                <ContextualHelpDrawer />
                {auth.isLoggedIn && <ImageLibraryPage />}
                <MinimizeImportProgress />
                <MinimizeBulkInvitesProgress />
            </Router>
        )
}

export default App
